import { useOutsideClick } from '@sequencehq/utils'
import {
  Text,
  GreyGrey20,
  GreyGrey60,
  GreyGrey80,
  Primary13SemiBold,
  Primary14SemiBold,
  IconArrowRight,
  Button,
  TextInput,
  Flex
} from '@sequencehq/core-components'
import { usePhaseDuration } from './usePhaseDuration'
import { usePhaseName } from './usePhaseName'
import { useLayoutEffect, useRef, useState } from 'react'
import { usePhasePermissions } from 'Cube/utils/usePhasePermissions'

const NameInput = (props: {
  initialValue: string
  onSubmit: (value: string) => void
  onCancel: () => void
}) => {
  const [name, setName] = useState<string>(props.initialValue)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLInputElement>(null)

  useOutsideClick({
    ref: wrapperRef,
    handler: () => props.onSubmit(name)
  })

  /**
   * Focus the input on mount
   */
  useLayoutEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <Flex
      gap="8px"
      width="100%"
      onClick={e => {
        e.stopPropagation()
      }}
      ref={wrapperRef}
    >
      <TextInput
        ref={inputRef}
        height="32px"
        value={name}
        onChange={newValue => setName(newValue)}
        width="100%"
        placeholder="Phase name"
        onKeyDown={e => {
          if (e.key === 'Enter') {
            props.onSubmit(name)
          }
        }}
        data-testid="phase.name.input"
      />
      <Button
        variant="secondary"
        onClick={() => {
          props.onCancel()
        }}
        data-testid="phase.name.cancel"
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={() => {
          props.onSubmit(name)
        }}
        data-testid="phase.name.submit"
      >
        Save
      </Button>
    </Flex>
  )
}

export const PhaseTitle = (props: {
  phaseId: string
  isEditing: boolean
  onEditStart: () => void
  onEditComplete: () => void
}) => {
  const phaseDuration = usePhaseDuration({ phaseId: props.phaseId })
  const phaseName = usePhaseName({ phaseId: props.phaseId })
  const { canUpdatePhase } = usePhasePermissions()

  if (props.isEditing) {
    return (
      <NameInput
        initialValue={phaseName.name}
        onSubmit={newValue => {
          phaseName.updateName(newValue)
          props.onEditComplete()
        }}
        onCancel={() => props.onEditComplete()}
      />
    )
  }

  if (phaseName.name) {
    return (
      <Flex alignItems="center" width="fit-content" gap="8px">
        <Text
          {...Primary13SemiBold}
          color={GreyGrey80}
          padding="8px 4px"
          {...(phaseName.available
            ? {
                _hover: {
                  background: GreyGrey20,
                  borderRadius: '4px',
                  cursor: 'text'
                },
                onClick: e => {
                  e.stopPropagation()
                  if (canUpdatePhase) {
                    props.onEditStart()
                  }
                }
              }
            : {})}
          data-testid="phase.name"
        >
          {phaseName.name}
        </Text>

        {phaseDuration.startDateFormatted && phaseDuration.endDateFormatted ? (
          <Flex gap={phaseName.name ? '4px' : '8px'} alignItems="center">
            <Text
              {...Primary13SemiBold}
              color={GreyGrey60}
              data-testid="phase.dates.start"
            >
              {phaseDuration.startDateFormatted}
            </Text>

            <IconArrowRight
              color={GreyGrey60}
              style={{ position: 'relative', top: '1px' }}
            />

            <Text
              {...Primary13SemiBold}
              color={GreyGrey60}
              data-testid="phase.dates.end"
            >
              {phaseDuration.endDateFormatted}
            </Text>
          </Flex>
        ) : (
          <Text {...Primary13SemiBold} color={GreyGrey60}>
            {phaseDuration.differenceFormatted}
          </Text>
        )}
      </Flex>
    )
  }

  return (
    <Flex
      alignItems="center"
      gap="8px"
      padding="8px 4px"
      data-testid="phase.name"
      {...(phaseName.available
        ? {
            _hover: {
              background: GreyGrey20,
              borderRadius: '4px',
              cursor: 'text'
            },
            onClick: e => {
              e.stopPropagation()
              if (canUpdatePhase) {
                props.onEditStart()
              }
            }
          }
        : {})}
      width="fit-content"
    >
      {phaseDuration.startDateFormatted && phaseDuration.endDateFormatted ? (
        <Flex gap="4px" alignItems="center">
          <Text
            {...Primary14SemiBold}
            color={GreyGrey80}
            data-testid="phase.dates.start"
          >
            {phaseDuration.startDateFormatted}
          </Text>

          <IconArrowRight
            color={GreyGrey80}
            style={{ position: 'relative', top: '1px' }}
          />

          <Text
            {...Primary14SemiBold}
            color={GreyGrey80}
            data-testid="phase.dates.end"
          >
            {phaseDuration.endDateFormatted}
          </Text>
        </Flex>
      ) : (
        <Text {...Primary14SemiBold} color={GreyGrey80}>
          {phaseDuration.differenceFormatted}
        </Text>
      )}
    </Flex>
  )
}
